import React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import {
  AccountingOverviewPageDocument,
  AccountingOverviewPageQuery,
} from 'generated/resources/graphql'

import { DateNavigator } from 'modules/core/components/DateNavigator'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { UpsellModuleBanner } from 'modules/finance/components/Overview/_UpsellModuleBanner'
import { useOverviewPage } from 'modules/finance/components/Overview/common'
import { buildAccountingDraftEntriesDashboardWidget } from 'modules/finance/components/Widgets/AccountingDraftEntriesWidget'
import { buildPendingActionsWidget } from 'modules/finance/components/Widgets/PendingActionsWidget'
import { buildReconciliationWidget } from 'modules/finance/components/Widgets/ReconciliationWidget'
import { WidgetDashboard } from 'modules/finance/components/Widgets/common/widgets'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const accountingWidgets = (
  dashboardFilters: { endDate: string; startDate: string; currency: string; companyId: string },
  widgetVisibilityChecks: {
    showAccountingWidgets: boolean
    showReconciliationWidget: boolean
  }
) => {
  if (
    !widgetVisibilityChecks.showAccountingWidgets &&
    !widgetVisibilityChecks.showReconciliationWidget
  ) {
    return []
  }
  const { endDate, startDate, companyId, currency } = dashboardFilters

  return [
    buildPendingActionsWidget(['accounting']),
    widgetVisibilityChecks.showAccountingWidgets &&
      widgetVisibilityChecks.showReconciliationWidget &&
      buildReconciliationWidget({
        queryArgs: {
          filter: {
            bookingDate: { between: [startDate, endDate] },
            currency: { eq: currency },
          },
        },
        prepareDataArgs: {
          displayedFilters: [],
        },
      }),
    widgetVisibilityChecks.showAccountingWidgets &&
      buildAccountingDraftEntriesDashboardWidget({
        queryArgs: {
          companyId: companyId,
          filter: {
            referenceDate: { between: [startDate, endDate] },
          },
          filterEntry: {
            referenceDate: { between: [startDate, endDate] },
            status: { in: ['published', 'reversed'] },
          },
        },
        prepareDataArgs: {
          displayedFilters: [],
        },
      }),
  ].filter(Boolean)
}

const buildWidgetVisibilityChecks = (data?: AccountingOverviewPageQuery) => {
  const canManageJournalEntries = data?.finance?._policies?.journalEntriesManage?.can ?? false
  const canManageReconciliationsWithJournalEntries =
    data?.banking?._policies?.reconciliationsSeeJournalEntriesPage?.can ?? false
  const canManageReconciliationsWithFinancialDocuments =
    data?.banking?._policies?.reconciliationsSeeFinancialDocumentsPage?.can ?? false
  const canManageReconciliations =
    canManageReconciliationsWithJournalEntries || canManageReconciliationsWithFinancialDocuments

  return {
    showAccountingWidgets: canManageJournalEntries,
    showReconciliationWidget: canManageReconciliations,
  }
}

const AccountingOverviewPage = () => {
  const { data, loading, error } = useQuery(AccountingOverviewPageDocument, {
    variables: {
      featureIds: [
        Features.accounts_payable_management,
        Features.expenses_core,
        Features.procurement_core,
        Features.it_management_management,
      ],
    },
  })

  const canAccessOverview =
    data?.finance._policies.sidebarSeeFinanceOverviewSubLevelItem?.can ?? false

  const {
    missingModule,
    moduleUpsellBannerHidden,
    setModuleUpsellBannerHidden,
    interval,
    onDateRangeChange,
    dashboardFilters,
  } = useOverviewPage(data?.companies)

  const widgetVisibilityChecks = buildWidgetVisibilityChecks(data)

  useUnsafeEffect(() => {
    if (!loading && data && canAccessOverview) {
      const checks = widgetVisibilityChecks
      track(trackingEvents.accounting.overview.sectionView, {
        show_accounting_widgets: checks.showAccountingWidgets,
      })
    }
  }, [loading, data, canAccessOverview, widgetVisibilityChecks])

  return (
    <ResponsivePage.Body
      variant="full"
      error={error}
      loading={!data}
      authorized={canAccessOverview}
    >
      <Box flexDirection="column" gap="s32">
        {missingModule && !moduleUpsellBannerHidden && (
          <UpsellModuleBanner
            source="ap_accounting_overview_upsell_card"
            content={i18n.t('finance.overview.upsell.banner.description')}
            title={i18n.t('finance.overview.upsell.banner.title')}
            missingModule={missingModule.module}
            missingFeature={missingModule.feature}
            onHide={() => setModuleUpsellBannerHidden(true)}
          />
        )}

        <DateNavigator
          startDate={interval.startDate}
          endDate={interval.endDate}
          onDateRangeChange={onDateRangeChange}
        />
        <WidgetDashboard widgets={accountingWidgets(dashboardFilters, widgetVisibilityChecks)} />
      </Box>
    </ResponsivePage.Body>
  )
}

export default AccountingOverviewPage
